import type {
    IWidgetProperties,
    IWidgetPos,
} from "app/components/dashboard/dashboard-templates/DashboardTemplateService";
import { DefaultFetchService } from "app/services/fetchService";

import type { IDashboard } from "../components/dashboard/services/IDashboard";

const fetchService = DefaultFetchService.getInstance();

interface IWidgetResponse {
    addedTime: string;
    dashboardId: string;
    id: string;
    pos: IWidgetPos;
    properties: IWidgetProperties;
}
interface IUserResourceNew {
    addDashboard: (dashboard: IDashboard) => Promise<IDashboard>;
    duplicateDashboard: (dashboardId: string, newDashboardName: string) => Promise<IDashboard>;
    bulkAddDashboards: (dashboards: Array<IDashboard>) => Promise<Array<IDashboard>>;
    updateDashboard: (dashboard: IDashboard) => Promise<IDashboard>;
    updateDashboardWidgets: (dashboard: IDashboard) => Promise<IDashboard>;
    deleteDashboard: (dashboard: IDashboard) => Promise<{ data: string }>;
    addWidget: (widget: any) => Promise<IWidgetResponse>;
    updateWidget: (widget: any) => Promise<IWidgetResponse>;
    deleteWidget: (id: string, dashboardId?: string) => Promise<void>;
    htmlToPdf: (data: any) => Promise<any>;
    htmlToPng: (data: any) => Promise<any>;
}

interface IduplicateDashboardTo {
    Id: string; // dashboard ID
    Name: string; // new dashboard name
    UserId: string;
}
const onSuccess = (data: FixmeType) => {
    return {
        success: true,
        payload: data,
    };
};

const onFailure = (errMsg: FixmeType) => {
    return {
        success: false,
        payload: errMsg,
    };
};

export async function duplicateDashboardTo(data: IduplicateDashboardTo) {
    const res = await fetchService.post("/api/userdata/dashboards/duplicateTo", data).then(onSuccess).catch(onFailure);
    return res;
}

const widgetRequestTrasnformer = (data: FixmeType) => {
    const newData = { ...data };
    if (typeof data.properties === "object" && data.properties !== null) {
        newData.properties = JSON.stringify(data.properties);
    }

    if (typeof data.pos === "object" && data.pos !== null) {
        newData.pos = JSON.stringify(data.pos);
    }
    return newData;
};

const widgetResponseTransform = (response: FixmeType) => {
    const data = response.data;
    data.pos = JSON.parse(data.pos) || JSON.parse(data.position) || {};
    data.properties = JSON.parse(data.properties);
    if (data.position) {
        delete data.position;
    }
    return data;
};

// Converts resource data into FormData object (https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects).
// This was originally made in order to solve the issue of huge PDF markup.
const formDataRequestTransformer = (data: FixmeType) => {
    if (typeof data === "undefined") {
        return data;
    }

    const fd = new FormData();
    data.forEach((value: FixmeType, key: string) => {
        if (value instanceof FileList) {
            if (value.length === 1) {
                fd.append(key, value[0]);
            } else {
                for (let i = 0; i < value.length; i++) {
                    fd.append(`${key}_${i}`, value[i]);
                }
            }
        } else {
            fd.append(key, value);
        }
    });

    return fd;
};

const addDashboard = async (dashboard: IDashboard): Promise<IDashboard> => {
    const response: any = await fetchService.post("/api/userdata/dashboards/add", dashboard);
    return response.data;
};

const duplicateDashboard = async (dashboardId: string, newDashboardName: string): Promise<IDashboard> => {
    const requestBody = {
        id: dashboardId,
        name: newDashboardName,
    };

    const response: any = await fetchService.post("/api/userdata/dashboards/duplicate", requestBody);
    return response.data;
};

const bulkAddDashboards = async (dashboards: IDashboard[]): Promise<Array<IDashboard>> => {
    const response: any = await fetchService.post("/api/userdata/dashboards/add/bulk", dashboards);
    return response.data;
};

const updateDashboard = async (dashboard: IDashboard): Promise<IDashboard> => {
    const response: any = await fetchService.post("/api/userdata/dashboards/update", dashboard);
    return response.data;
};

const updateDashboardWidgets = async (dashboard: IDashboard): Promise<IDashboard> => {
    const response: any = await fetchService.post("/api/userdata/dashboards/updatewidgets", dashboard);
    return response.data;
};

const deleteDashboard = async (dashboard: IDashboard): Promise<{ data: string }> => {
    const response: any = await fetchService.post(`/api/userdata/dashboards/remove/${dashboard.id}`);
    return response;
};

const addWidget = async (widget: any): Promise<IWidgetResponse> => {
    const stringData = widgetRequestTrasnformer(widget);
    const response = await fetchService.post("/api/userdata/widgets/add", stringData);
    return widgetResponseTransform(response);
};

const updateWidget = async (widget: any): Promise<IWidgetResponse> => {
    const stringData = widgetRequestTrasnformer(widget);
    const response = await fetchService.post("/api/userdata/widgets/update", stringData);
    return widgetResponseTransform(response);
};

const deleteWidget = async (id: string, dashboardId?: string) => {
    await fetchService.post(`/api/userdata/widgets/remove/${id}`, {});
};

const htmlToPdf = async (data: FixmeType) => {
    const formData = formDataRequestTransformer(data);
    const response: any = await fetchService.post("CustomPfd/GetCustomPdf", formData, {
        headers: {
            "Content-Type": undefined,
        },
    });
    return response.data;
};

const htmlToPng = async (data: FixmeType) => {
    const formData = formDataRequestTransformer(data);
    const response: any = await fetchService.post("CustomPng/GetCustomPng", formData, {
        headers: {
            "Content-Type": undefined,
        },
    });
    return response.data;
};

export const UserResource: IUserResourceNew = {
    addDashboard,
    duplicateDashboard,
    bulkAddDashboards,
    updateDashboard,
    updateDashboardWidgets,
    deleteDashboard,
    addWidget,
    updateWidget,
    deleteWidget,
    htmlToPdf,
    htmlToPng,
};
